import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Link } from 'react-router-dom';
import { Wallet, BookOpen, ShoppingCart, Truck, ArrowRight, Package } from 'lucide-react';

export default function SchoolDashboard() {
  return (
    <div className="space-y-8 animate-fade-in">
      {/* Welcome Banner */}
      <Card className="bg-gradient-to-r from-[#1E4D35] to-[#2E6D4A] text-white border-0">
        <CardContent className="py-6">
          <h2 className="font-heading text-xl font-semibold mb-2">
            Welcome back, Mrs Tan. CDS Cycle 1 2028 selection closes in 5 days.
          </h2>
          <p className="text-white/80">Complete your title selection to ensure timely delivery starting January 2028.</p>
        </CardContent>
      </Card>

      {/* Budget KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <Wallet className="w-5 h-5 text-[#1E4D35]" />
              <span className="text-sm text-slate-500">Total Budget</span>
            </div>
            <div className="text-2xl font-bold text-slate-900">$3,000</div>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-sm text-slate-500">Budget Used</span>
            </div>
            <div className="text-2xl font-bold text-emerald-600">$2,847.50</div>
            <Progress value={94.9} className="mt-2" />
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-sm text-slate-500">Remaining</span>
            </div>
            <div className="text-2xl font-bold text-amber-600">$152.50</div>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <BookOpen className="w-5 h-5 text-[#1E4D35]" />
              <span className="text-sm text-slate-500">Selection</span>
            </div>
            <Badge className="bg-emerald-100 text-emerald-700 text-sm">✓ Submitted</Badge>
          </CardContent>
        </Card>
      </div>

      {/* Active Orders */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Active Orders</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* CDS Cycle 1 2028 */}
          <div className="p-4 border rounded-lg hover:shadow-sm transition-shadow">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5 text-[#1E4D35]" />
                  <span className="font-semibold">CDS Cycle 1 2028 — 91 books</span>
                </div>
                <p className="text-sm text-slate-500 mt-1">Order No: PO-RGS-CDS-C1-2028 | Delivery: Jan–May 2028</p>
              </div>
              <Badge className="bg-emerald-100 text-emerald-700">✓ Confirmed</Badge>
            </div>
            <div className="mt-3">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-500 w-[40%]" />
                </div>
                <span>Batch 2 of 5 in transit</span>
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button asChild size="sm" variant="outline">
                <Link to="/school/orders">View Full Order</Link>
              </Button>
              <Button asChild size="sm" className="bg-[#1E4D35]">
                <Link to="/school/orders">Track Deliveries</Link>
              </Button>
            </div>
          </div>

          {/* Pilot Cycle 2 */}
          <div className="p-4 border rounded-lg hover:shadow-sm transition-shadow">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-amber-600" />
                  <span className="font-semibold">Pilot Cycle 2 — 60 books (3 batches)</span>
                </div>
                <p className="text-sm text-slate-500 mt-1">Order No: PO-RGS-PILOT-C2 | Nov 2026 – Feb 2027</p>
              </div>
              <Badge className="bg-blue-100 text-blue-700">🚚 In Transit</Badge>
            </div>
            <Button size="sm" variant="outline" className="mt-3">
              <Truck className="w-4 h-4 mr-1" /> Track Pilot Delivery
            </Button>
          </div>

          {/* Pilot Cycle 1 Complete */}
          <div className="p-4 border rounded-lg bg-slate-50">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-slate-600">Pilot Cycle 1 — 60 books — COMPLETE</span>
                </div>
                <p className="text-sm text-slate-500 mt-1">Order No: PO-RGS-PILOT-C1 | Aug–Oct 2026</p>
              </div>
              <Badge className="bg-slate-200 text-slate-600">Complete</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Deliveries */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Recent Deliveries</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium">Oct 2026 Pilot C1 Batch 3</p>
                <p className="text-sm text-slate-500">20 books — Acknowledged 28 Oct</p>
              </div>
              <Badge className="bg-emerald-100 text-emerald-700">✓</Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium">Sep 2026 Pilot C1 Batch 2</p>
                <p className="text-sm text-slate-500">20 books — Acknowledged 29 Sep</p>
              </div>
              <Badge className="bg-emerald-100 text-emerald-700">✓</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Announcements */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Announcements</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <p className="p-3 bg-blue-50 rounded-lg text-blue-800">
              📅 CDS Cycle 2 2028 selection opens Jun 2026
            </p>
            <p className="p-3 bg-slate-50 rounded-lg">
              💡 Have book suggestions? Submit titles for Cycle 2 consideration
            </p>
            <p className="p-3 bg-slate-50 rounded-lg">
              📦 PPU non-CDS books accepted for cataloguing with monthly delivery
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
